import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { Camera, Upload, X, CheckCircle, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { otpAPI, appointmentsAPI } from '../../utils/api';
import CalendarView from './CalendarView';
import axios from 'axios';

const BookingForm = ({ center: propCenter, slot: propSlot }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { register, handleSubmit, watch } = useForm();

  const selectedNationality = watch('nationality_id');
  const selectedServiceId = watch('service_id');

  const showSidePanel = Boolean(selectedNationality && selectedServiceId);

  // FIX: Initialize center and slot state
  const [tempCenter, setTempCenter] = useState(propCenter || location.state?.center);
  const [tempSlot, setTempSlot] = useState(propSlot || location.state?.slot);

  const [servicesMenu, setServicesMenu] = useState([]);
  const [selectedServiceGroup, setSelectedServiceGroup] = useState(null);
  const [nationalities, setNationalities] = useState([]);

  const [calculatedFees, setCalculatedFees] = useState([]);
  const [documentsHTML, setDocumentsHTML] = useState('');
  const [feeLoading, setFeeLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);

  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [appointmentId, setAppointmentId] = useState('');

  // FIX: Add missing state for calendar modal
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  const email = watch('email');

  React.useEffect(() => {
    if (!tempCenter || !tempSlot) navigate('/');
  }, [tempCenter, tempSlot, navigate]);

  React.useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/nationalities`)
      .then(res => setNationalities(res.data || []))
      .catch(err => console.error('Failed to load nationalities:', err));
  }, []);

  React.useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/services-menu`)
      .then(res => setServicesMenu(res.data || []))
      .catch(err => console.error('Failed to load services:', err));
  }, []);

  React.useEffect(() => {
    if (!selectedNationality || !selectedServiceId) {
      setCalculatedFees([]);
      setDocumentsHTML('');
      return;
    }

    setFeeLoading(true);

    Promise.all([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/fees`, {
        params: { nationality_id: selectedNationality, service_id: selectedServiceId }
      }),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/services/${selectedServiceId}`)
    ])
      .then(([feeRes, serviceRes]) => {
        setCalculatedFees(feeRes.data || []);
        setDocumentsHTML(serviceRes.data?.content?.documents || '');
      })
      .catch(err => console.error('Failed to load fees/documents:', err))
      .finally(() => setFeeLoading(false));
  }, [selectedNationality, selectedServiceId]);

  // FIX: Add missing handlers
  const handleChangeDateTime = () => {
    setShowCalendarModal(true);
  };

  const handleSlotUpdate = (newSlot) => {
    setTempSlot(newSlot);
    setShowCalendarModal(false);
    toast.success('Date and time updated');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG and PNG files are allowed');
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;

    setPhotoPreview(imageSrc);

    // Convert base64 to file
    fetch(imageSrc)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], 'webcam-photo.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
      });

    setShowWebcam(false);
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendOTP = async () => {
    if (!email) return toast.error('Enter email');
    try {
      setLoading(true);
      await otpAPI.send(email);
      setOtpSent(true);
      toast.success('OTP sent to your email');
    } catch (err) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otp = watch('otp');
    if (!otp) return toast.error('Enter OTP');

    try {
      setLoading(true);
      await otpAPI.verify({ email, otp });
      setOtpVerified(true);
      toast.success('Email verified successfully');
    } catch (err) {
      toast.error('Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!otpVerified) return toast.error('Verify OTP first');

    try {
      setLoading(true);

      let photoPath = null;
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/upload-photo`,
          formData
        );
        photoPath = res.data?.image?.preview_url || null;
      }

      const payload = {
        center_id: tempCenter.id,
        appointment_date: tempSlot.date,
        time_slot: tempSlot.timeSlot,

        // 👤 REQUIRED BY MODEL
        first_name: data.first_name,
        last_name: data.last_name,
        nationality: data.nationality_id,      // or name if backend expects string
        date_of_birth: data.date_of_birth,

        // 🧾 SERVICE
        service_group_id: data.service_group_id,
        service_id: selectedServiceId,

        // 🛂 PASSPORT
        passport_number: data.passport_number,

        // 📞 CONTACT
        isd_code: data.isd_code,
        mobile_number: data.mobile_number,
        email: data.email,
        otp: data.otp,

        // 📸 PHOTO (NOW WORKING)
        photo_path: photoPath
      };

      const res = await appointmentsAPI.create(payload);
      setReferenceId(res.data.reference_id);
      setAppointmentId(res.data.id);
      setBookingSuccess(true);
      toast.success('Booking confirmed!');
    } catch (err) {
      toast.error('Booking failed. Please try again.');
      console.error('Booking error:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/appointments/${appointmentId}/pdf`,
        { responseType: 'blob' }
      );

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `Appointment_${referenceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed', err);
      toast.error('Failed to download PDF');
    }
  };

  if (bookingSuccess) {
    return (
      <div className="max-w-xl mx-auto bg-white p-8 rounded-lg shadow">
        <CheckCircle className="mx-auto h-16 w-16 text-green-500" />

        <h2 className="text-center mt-4 text-2xl font-bold">
          Booking Confirmed!
        </h2>

        <p className="text-center mt-2 text-gray-600">
          Reference ID:
        </p>

        <p className="text-center mt-1 font-mono text-xl font-bold">
          {referenceId}
        </p>

        <Button
          className="w-full mt-6"
          onClick={handleDownloadPDF}
        >
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      </div>
    );
  }

  return (
    <motion.div className="max-w-7xl mx-auto p-4">
      <div className={`grid gap-8 transition-all ${showSidePanel ? 'grid-cols-12' : 'grid-cols-1'}`}>

        {/* FORM */}
        <motion.div layout className={`bg-white p-8 rounded-lg shadow ${showSidePanel ? 'col-span-7' : ''}`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-primary">Complete Your Booking</h2>
              <p className="text-muted-foreground mt-1">
                {tempCenter?.name} - {tempSlot?.date} at {tempSlot?.timeSlot}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleChangeDateTime}>
              Change Date/Time
            </Button>
          </div>

          {/* Inline Calendar Modal */}
          <AnimatePresence>
            {showCalendarModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={() => setShowCalendarModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-2xl font-bold text-primary">Change Date & Time</h3>
                      <button
                        onClick={() => setShowCalendarModal(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>
                    <CalendarView
                      center={tempCenter}
                      onSelectSlot={handleSlotUpdate}
                      onBack={() => setShowCalendarModal(false)}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  {...register('first_name', { required: true })}
                  className="h-12"
                />
              </div>

              <div>
                <Label>Last Name *</Label>
                <Input
                  {...register('last_name', { required: true })}
                  className="h-12"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nationality *</Label>
                <select
                  {...register('nationality_id', { required: true })}
                  className="h-12 w-full rounded-md border border-input bg-background px-3"
                >
                  <option value="">Select Nationality</option>
                  {nationalities.map(n => (
                    <option key={n.id} value={n.id}>
                      {n.name}
                    </option>
                  ))}
                </select>
              </div>

             <div>
  <Label>Date of Birth *</Label>
  <Input
    type="date"
    max={new Date().toISOString().split("T")[0]}
    {...register('date_of_birth', { required: true })}
    className="h-12 w-full"
  />
</div>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Service Category */}
              <div>
                <Label>Service Category *</Label>
                <select
                  {...register('service_group_id', { required: true })}
                  className="h-12 w-full rounded-md border border-input bg-background px-3"
                  onChange={(e) => {
                    const group = servicesMenu.find(g => g.id === e.target.value);
                    setSelectedServiceGroup(group);
                  }}
                >
                  <option value="">Select Service Category</option>
                  {servicesMenu.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service Type */}
              <div>
                <Label>Service Type *</Label>
                <select
                  {...register('service_id', { required: true })}
                  className="h-12 w-full rounded-md border border-input bg-background px-3"
                  disabled={!selectedServiceGroup}
                >
                  <option value="">Select Service Type</option>
                  {selectedServiceGroup?.services?.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-4">
              {/* Passport Number */}
              <div className="col-span-3">
                <Label>Passport Number *</Label>
                <Input
                  {...register('passport_number', { required: true })}
                  className="h-12 w-full"
                  placeholder="Enter passport number"
                />
              </div>

              {/* ISD Code */}
              <div className="col-span-1">
                <Label htmlFor="isd_code">ISD Code *</Label>
                <Input
                  id="isd_code"
                  {...register('isd_code', { required: true })}
                  placeholder="+82"
                  className="h-12 w-full"
                />
              </div>

              {/* Mobile Number */}
              <div className="col-span-2">
                <Label htmlFor="mobile_number">Mobile Number *</Label>
                <Input
                  id="mobile_number"
                  {...register('mobile_number', { required: true })}
                  className="h-12 w-full"
                  placeholder="Enter mobile number"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  type="email"
                  {...register('email', { required: true })}
                  className="h-12"
                  disabled={otpVerified}
                  placeholder="Enter email address"
                />
                <Button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading || otpVerified}
                  className="whitespace-nowrap"
                >
                  Send OTP
                </Button>
              </div>
            </div>

            {otpSent && !otpVerified && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                <Label htmlFor="otp">OTP Code *</Label>
                <div className="flex gap-2">
                  <Input
                    id="otp"
                    {...register('otp')}
                    className="h-12 font-mono"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                  />
                  <Button
                    type="button"
                    onClick={handleVerifyOTP}
                    disabled={loading}
                  >
                    Verify
                  </Button>
                </div>
              </motion.div>
            )}

            {otpVerified && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="border-t pt-6"
                >
                  <Label>Applicant Photograph (Optional)</Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload a recent photograph or take one using your camera
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-3 rounded-md mb-4">
  <p className="text-sm font-medium">
    ⚠️ Note: Please upload photograph with white background
  </p>
</div>


                  {!photoPreview && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="h-32 flex-col"
                      >
                        <Upload className="h-8 w-8 mb-2" />
                        <span>Upload Photo</span>
                        <span className="text-xs text-muted-foreground mt-1">
                          Max 2MB, JPEG/PNG
                        </span>
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={handleFileUpload}
                        className="hidden"
                      />

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowWebcam(true)}
                        className="h-32 flex-col"
                      >
                        <Camera className="h-8 w-8 mb-2" />
                        <span>Take Photo</span>
                        <span className="text-xs text-muted-foreground mt-1">
                          Use camera
                        </span>
                      
                      </Button>
                    </div>
                  )}

                  {photoPreview && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="relative inline-block"
                    >
                      <img
                        src={photoPreview}
                        alt="Applicant"
                        className="w-48 h-48 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </motion.div>
                  )}

                  <AnimatePresence>
                    {showWebcam && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                      >
                        <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Take Photo</h3>
                            <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-1 rounded">
  ⚠️ Note: Please upload photograph with white background
</p>

                            <button
                              onClick={() => setShowWebcam(false)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <X className="h-6 w-6" />
                            </button>
                          </div>
                          <Webcam
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            className="w-full rounded-lg mb-4"
                          />
                          <div className="flex justify-end space-x-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowWebcam(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={capturePhoto}
                              className="bg-accent hover:bg-accent/90"
                            >
                              <Camera className="h-4 w-4 mr-2" />
                              Capture
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </AnimatePresence>
            )}

            <Button
              type="submit"
              disabled={loading || !otpVerified}
              size="lg"
              className="w-full bg-accent hover:bg-accent/90"
            >
              {loading ? 'Processing...' : 'Confirm Booking'}
            </Button>
          </form>
        </motion.div>

        {/* SIDE PANEL */}
        <AnimatePresence>
          {showSidePanel && (
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 40 }}
              className="col-span-5"
            >
              <div className="sticky top-24 space-y-6">

                <div className="bg-slate-50 border rounded-lg p-6">
                  <h3 className="font-semibold mb-4">Applicable Fees</h3>

                  {feeLoading ? (
                    <p className="text-sm text-gray-500">Calculating…</p>
                  ) : calculatedFees.length > 0 ? (
                    calculatedFees.map(f => {
                      const consular = f.fee_components?.consular_fee ?? 0;
                      const icwf = f.fee_components?.icwf_fee ?? 0;
                      const service = f.fee_components?.service_fee ?? 0;
                      const total = consular + icwf + service;

                      return (
                        <div
                          key={f.id}
                          className="space-y-2 border-b last:border-b-0 pb-4 last:pb-0"
                        >
                          <div className="flex justify-between text-sm">
                            <span>Consular Fee</span>
                            <span>{f.currency} {consular.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span>ICWF Fee</span>
                            <span>{f.currency} {icwf.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span>Service Fee</span>
                            <span>{f.currency} {service.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between font-semibold pt-2 border-t">
                            <span>Total (per applicant)</span>
                            <span>{f.currency} {total.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500">No fees available</p>
                  )}
                </div>

                {documentsHTML && (
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="font-semibold mb-2">Required Documents</h3>
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: documentsHTML }}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </motion.div>
  );
};

export default BookingForm;